import type { DocPageContent } from '../types';

/**
 * "Users & audiences" — Dashboard section (ADR-0074).
 *
 * Reconciled from apps/web `features/users` (routes `/users`, `/users/:sub`,
 * `/users/audiences/:id`) and the `users.*` / `audiences.*` / `userlearning.*`
 * catalogs.
 *
 * The page is deliberately explicit that the roster is EMPTY until the customer
 * calls `identify`, because an empty page with no explanation reads as a broken
 * feature — which is exactly what the in-product checklist exists to correct.
 */
export const DASHBOARD_USERS_PAGE: DocPageContent = {
  slug: 'dashboard/users',
  titleKey: 'docs.dashboard.users.title',
  descriptionKey: 'docs.dashboard.users.description',
  blocks: [
    { kind: 'paragraph', textKey: 'docs.dashboard.users.lead' },

    { kind: 'heading', level: 2, id: 'roster', textKey: 'docs.dashboard.users.roster.heading' },
    { kind: 'paragraph', textKey: 'docs.dashboard.users.roster.body' },
    {
      kind: 'callout',
      tone: 'important',
      titleKey: 'docs.dashboard.users.roster.empty.title',
      textKey: 'docs.dashboard.users.roster.empty.body',
      links: {
        auth: {
          target: { docSlug: 'auth/end-user-auth' },
          labelKey: 'docs.dashboard.users.roster.empty.link',
        },
      },
    },

    { kind: 'heading', level: 2, id: 'person', textKey: 'docs.dashboard.users.person.heading' },
    { kind: 'paragraph', textKey: 'docs.dashboard.users.person.body' },
    {
      kind: 'list',
      itemKeys: [
        'docs.dashboard.users.person.item.summary',
        'docs.dashboard.users.person.item.activity',
        'docs.dashboard.users.person.item.controls',
        'docs.dashboard.users.person.item.compliance',
      ],
    },

    { kind: 'heading', level: 2, id: 'memory', textKey: 'docs.dashboard.users.memory.heading' },
    { kind: 'paragraph', textKey: 'docs.dashboard.users.memory.body' },
    {
      kind: 'list',
      itemKeys: [
        'docs.dashboard.users.memory.item.verified',
        'docs.dashboard.users.memory.item.budget',
        'docs.dashboard.users.memory.item.retention',
        'docs.dashboard.users.memory.item.erase',
      ],
    },

    {
      kind: 'heading',
      level: 2,
      id: 'audiences',
      textKey: 'docs.dashboard.users.audiences.heading',
    },
    { kind: 'paragraph', textKey: 'docs.dashboard.users.audiences.body' },
    {
      kind: 'definitions',
      items: [
        {
          termKey: 'docs.dashboard.users.audiences.rules.term',
          descKey: 'docs.dashboard.users.audiences.rules.desc',
        },
        {
          termKey: 'docs.dashboard.users.audiences.fixed.term',
          descKey: 'docs.dashboard.users.audiences.fixed.desc',
        },
        {
          termKey: 'docs.dashboard.users.audiences.governance.term',
          descKey: 'docs.dashboard.users.audiences.governance.desc',
        },
      ],
    },

    { kind: 'heading', level: 2, id: 'precedence', textKey: 'docs.dashboard.users.order.heading' },
    { kind: 'paragraph', textKey: 'docs.dashboard.users.order.body' },
    {
      kind: 'callout',
      tone: 'note',
      titleKey: 'docs.dashboard.users.order.live.title',
      textKey: 'docs.dashboard.users.order.live.body',
    },

    { kind: 'heading', level: 2, id: 'next', textKey: 'docs.common.nextSteps' },
    {
      kind: 'cardGrid',
      cards: [
        {
          titleKey: 'docs.dashboard.users.next.auth.title',
          textKey: 'docs.dashboard.users.next.auth.text',
          to: { docSlug: 'auth/end-user-auth' },
        },
        {
          titleKey: 'docs.dashboard.users.next.prompts.title',
          textKey: 'docs.dashboard.users.next.prompts.text',
          to: { docSlug: 'dashboard/prompts' },
        },
        {
          titleKey: 'docs.dashboard.users.next.privacy.title',
          textKey: 'docs.dashboard.users.next.privacy.text',
          to: { docSlug: 'trust/privacy' },
        },
      ],
    },
  ],
};
